import {
  Box,
  Stack,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Button,
  Spacer,
  Divider,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { Util, Date as dateType } from '@finley-technologies/finley-types';

import { ConfigFormState } from '../types';
import {
  convertConfigFormStatesToRecurringDateConfigs,
  createNonRelativeConfigFormStatesToTestTriggerDate,
  downloadCSVWithoutHeader,
} from '../utils';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';

type ReviewDatesComponentProps = {
  config: dateType.RecurringDate.Config;
  setReviewDatesCount: (num: number) => void;
  onAddToExclusion?: (date: Util.FullDateStr) => void;
  onRemoveFromExclusion?: (date: Util.FullDateStr) => void;
  titleNode?: React.ReactNode;
  canEditManualDates?: boolean;
};
export const ReviewDatesComponent = ({
  config,
  setReviewDatesCount,
  onAddToExclusion = () => {},
  onRemoveFromExclusion = () => {},
  titleNode,
  canEditManualDates = true,
}: ReviewDatesComponentProps) => {
  const possibleDates = recurringDate.generateDateList(
    config.recurringDateConfigs,
  );
  const possibleExclusions = recurringDate.generateDateList(config.exclusions);

  const validDates = _.difference(possibleDates, possibleExclusions);
  const validDatesSorted = validDates.sort(
    (a, b) =>
      commonDate.fullDateStrToDate(a).getTime() -
      commonDate.fullDateStrToDate(b).getTime(),
  );
  const excludedDates = _.intersection(possibleDates, possibleExclusions);
  const excludedDatesSorted = excludedDates.sort(
    (a, b) =>
      commonDate.fullDateStrToDate(a).getTime() -
      commonDate.fullDateStrToDate(b).getTime(),
  );

  useEffect(() => setReviewDatesCount(validDatesSorted.length), [
    setReviewDatesCount,
    validDatesSorted.length,
  ]);

  return (
    <Stack spacing="20px">
      <HStack>
        {titleNode}
        <Spacer />
        {validDatesSorted && validDatesSorted.length > 0 && (
          <Button
            variant="v2-ghost"
            fontSize="sm"
            fontWeight="medium"
            leftIcon={<Icon icon="arrow-down-to-bracket" />}
            onClick={() => {
              downloadCSVWithoutHeader('recurring-ui-dates', validDatesSorted);
            }}
          >
            Download CSV
          </Button>
        )}
      </HStack>

      {validDatesSorted && validDatesSorted.length > 0 ? (
        <TableContainer>
          <Box overflowY="auto" maxHeight="300px">
            <Table>
              <Thead bg="gray.500">
                <Tr>
                  <Th>Dates</Th>
                  <Th>Days</Th>
                  {canEditManualDates && <Th />}
                </Tr>
              </Thead>
              <Tbody>
                {validDatesSorted.map((date) => {
                  return (
                    <Tr key={date}>
                      <Td>{commonDate.fullDateStrToHumanDateString(date)}</Td>
                      <Td>{commonDate.fullDateStrToStr(date, 'ccc')}</Td>
                      {canEditManualDates && (
                        <Td width="10%">
                          <IconButtonV2
                            icon={<Icon icon="xmark" />}
                            aria-label="Exclude"
                            variant="v2-ghost"
                            onClick={() => {
                              onAddToExclusion(date);
                            }}
                          />
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </TableContainer>
      ) : (
        <Text fontWeight="light">No valid dates to review.</Text>
      )}

      {excludedDatesSorted && excludedDatesSorted.length > 0 && (
        <>
          <Text fontSize="md" fontWeight="bold" mt={2}>
            Excluded Dates
          </Text>
          <TableContainer>
            <Box overflowY="auto" maxHeight="300px">
              <Table>
                <Thead bg="gray.500">
                  <Tr>
                    <Th>Dates</Th>
                    <Th>Days</Th>
                    {canEditManualDates && <Th />}
                  </Tr>
                </Thead>
                <Tbody>
                  {excludedDatesSorted.map((date) => {
                    return (
                      <Tr key={date}>
                        <Td>{commonDate.fullDateStrToHumanDateString(date)}</Td>
                        <Td>{commonDate.fullDateStrToStr(date, 'ccc')}</Td>
                        {canEditManualDates && (
                          <Td width="10%">
                            <IconButtonV2
                              icon={<Icon icon="plus" />}
                              aria-label="Add"
                              variant="v2-ghost"
                              onClick={() => {
                                onRemoveFromExclusion(date);
                              }}
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

type ReviewDatesComponentFromConfigFormStateProps = {
  recurringConfigs: ConfigFormState[];
  excludedConfigs: ConfigFormState[];
  manualAddDates: Util.FullDateStr[];
  manualExcludeDates: Util.FullDateStr[];
  setReviewDatesCount: (num: number) => void;
  onAddToExclusion?: (date: Util.FullDateStr) => void;
  onRemoveFromExclusion?: (date: Util.FullDateStr) => void;
  titleNode?: React.ReactNode;
  canEditManualDates?: boolean;
};
export const ReviewDatesComponentFromConfigFormState = ({
  recurringConfigs,
  excludedConfigs,
  manualAddDates,
  manualExcludeDates,
  setReviewDatesCount,
  onAddToExclusion = () => {},
  onRemoveFromExclusion = () => {},
  titleNode,
  canEditManualDates = true,
}: ReviewDatesComponentFromConfigFormStateProps) => {
  const allRecurringDates = convertConfigFormStatesToRecurringDateConfigs(
    recurringConfigs.filter((c) => c.isValid && !c.isRelative),
  ).concat(manualAddDates.length > 0 ? [manualAddDates] : []);
  const allExclusionDates = convertConfigFormStatesToRecurringDateConfigs(
    excludedConfigs.filter((c) => c.isValid && !c.isRelative),
  ).concat(manualExcludeDates.length > 0 ? [manualExcludeDates] : []);

  return (
    <ReviewDatesComponent
      config={{
        recurringDateConfigs: allRecurringDates,
        exclusions: allExclusionDates,
      }}
      setReviewDatesCount={setReviewDatesCount}
      onAddToExclusion={onAddToExclusion}
      onRemoveFromExclusion={onRemoveFromExclusion}
      titleNode={titleNode}
      canEditManualDates={canEditManualDates}
    />
  );
};

type TestTriggerDateProps = {
  relativeConfigs: ConfigFormState[];
  excludedConfigs: ConfigFormState[];
  preSelectedTriggerDate?: Util.FullDateStr;
};
export const TestTriggerDate = ({
  relativeConfigs,
  excludedConfigs,
  preSelectedTriggerDate,
}: TestTriggerDateProps) => {
  const [input, setInput] = React.useState<Util.FullDateStr | undefined>(
    preSelectedTriggerDate,
  );

  const [recurringConfigs, setRecurringConfigs] = React.useState<
    ConfigFormState[]
  >([]);
  const [exclusions, setExclusions] = React.useState<ConfigFormState[]>([]);

  // number of valid dates in review date accordion
  const [reviewDatesCount, setReviewDatesCount] = React.useState<number>(0);

  useEffect(() => {
    if (!input) {
      setRecurringConfigs([]);
      setExclusions([]);
      return;
    }

    setRecurringConfigs(
      createNonRelativeConfigFormStatesToTestTriggerDate(
        input,
        relativeConfigs,
      ),
    );
    setExclusions(
      createNonRelativeConfigFormStatesToTestTriggerDate(
        input,
        excludedConfigs,
      ),
    );
  }, [input, excludedConfigs, relativeConfigs]);

  return (
    <Stack gap="1rem">
      <Typography variant="section-title" trim>
        Preview Dates
      </Typography>

      <Typography variant="base-muted" trim>
        Provide a sample value for the variable to preview generated dates.
        Preview will appear below once all required values are entered.
      </Typography>

      <HStack width="full" mt="2rem">
        <Typography
          fontWeight="bold"
          variant="base"
          trim
          TextProps={{ width: '50%' }}
        >
          Variable
        </Typography>
        <Typography
          fontWeight="bold"
          variant="base"
          trim
          TextProps={{ width: '50%' }}
        >
          Sample Date
        </Typography>
      </HStack>

      <Divider />

      <HStack width="full">
        <Typography variant="base-muted" TextProps={{ width: '50%' }} trim>
          &quot;triggerDate&quot;
        </Typography>
        <SimpleDatePicker
          width="50%"
          dateFormat="MMMM dd, yyyy"
          selectedDate={input ? new Date(input) : undefined}
          isInvalid={input === undefined}
          showTimeSelect={false}
          isClearable={false}
          setSelectedDate={async (newDueDate: Date | null) => {
            if (newDueDate) {
              const newDueDateFullDateStr = commonDate.dateToFullDateString(
                newDueDate,
              );

              setInput(newDueDateFullDateStr);
            } else {
              setInput(undefined);
            }
          }}
        />
      </HStack>

      {recurringConfigs && recurringConfigs.length > 0 && (
        <ReviewDatesComponentFromConfigFormState
          recurringConfigs={recurringConfigs}
          excludedConfigs={exclusions}
          manualAddDates={[]}
          manualExcludeDates={[]}
          setReviewDatesCount={(num: number) => setReviewDatesCount(num)}
          titleNode={
            <Text fontSize="md" fontWeight="bold" pt={4} pb={2}>
              {`Review Dates (${reviewDatesCount})`}
            </Text>
          }
          canEditManualDates={false} // since this is a relative date config that we are testing, we cannot support manual add or exclude dates
        />
      )}
    </Stack>
  );
};
